import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import Table from '../../../components/Table';
import { GarageAllocationCard, TitleDiv, PickGarageDiv, AddAction, SmallP } from './styled';
import Title from '../../../components/Title';
import Clickable from '../../../components/Clickable';
import GaragePickerModal from './components/GaragePickerModal';
import getInvitationsTableColumns from './components/Columns';
import HiddenInvitationsToggle from './components/hiddenInvitationsToggle';
import { getSelectedJobInvite } from '../utils';
import GarageMembershipTag from './components/GarageMembershipTag';
import {
  getCustomerChoiceFailedNoteContent,
  shouldShowCustomerChoiceFailedTag,
  shouldShowMembershipTag,
} from '../../../../common/garageMembership';
import CustomerChoiceFailedTag from './components/CustomerChoiceFailedTag';
import { loadGarageOperationsForInvites, loadGaragesForBooking } from './api';
import { buildDisplayedBookingInvites } from './inviteDisplay';

const EMPTY_GARAGE_INVITES = [];

function GarageAllocation({ booking, onGarageOperationsLoaded }) {
  const [popupOpen, setPopupOpen] = useState(false);
  const [expandedInvites, setExpandedInvites] = useState(false);
  const [garages, setGarages] = useState([]);
  const [loadingGarages, setLoadingGarages] = useState(false);
  const garageInvites = booking.garageInvites || EMPTY_GARAGE_INVITES;
  const modalBooking = useMemo(() => ({ ...booking, garageInvites }), [booking, garageInvites]);

  useEffect(() => {
    let isMounted = true;

    loadGarageOperationsForInvites(booking)
      .then((garageOperations) => {
        if (!isMounted) return;
        onGarageOperationsLoaded(garageOperations);
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      isMounted = false;
    };
  }, [booking, onGarageOperationsLoaded]);

  const openPopup = useCallback(async () => {
    setPopupOpen(true);
    setLoadingGarages(true);

    try {
      const { garages, garageOperations } = await loadGaragesForBooking(booking);
      setGarages(garages);
      onGarageOperationsLoaded(garageOperations);
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || 'Unable to load garages');
      setPopupOpen(false);
    } finally {
      setLoadingGarages(false);
    }
  }, [booking, onGarageOperationsLoaded]);

  const displayedBookingInvites = useMemo(
    () => buildDisplayedBookingInvites(garageInvites, expandedInvites),
    [expandedInvites, garageInvites]
  );

  const invitationsTableColumns = getInvitationsTableColumns({
    garageInvites,
    booking,
  });

  const getRowClassName = ({ invitationId }) => {
    const selectedJobInvite = getSelectedJobInvite({
      garageInvites,
      masterStatusId: booking.masterStatusId,
    });
    const invitationIdBeingViewed = (selectedJobInvite || booking).invitationId;
    return invitationId === invitationIdBeingViewed ? 'selected-invite' : '';
  };

  const showMembershipTag = shouldShowMembershipTag(booking, garageInvites);
  const showCustomerChoiceFailedTag = shouldShowCustomerChoiceFailedTag(booking, garageInvites);

  return (
    <GarageAllocationCard>
      <TitleDiv>
        <Title>Garage invitation</Title>
        {showMembershipTag && <GarageMembershipTag />}
        <PickGarageDiv>
          <Clickable>
            <AddAction onClick={openPopup} iconClass="icon-add-circle-1">
              <SmallP>Select a garage</SmallP>
            </AddAction>
          </Clickable>
        </PickGarageDiv>
        {showCustomerChoiceFailedTag && (
          <CustomerChoiceFailedTag noteContent={getCustomerChoiceFailedNoteContent(booking)} />
        )}
      </TitleDiv>
      <HiddenInvitationsToggle
        garageInvitesLength={garageInvites.length}
        isExpandedInvites={expandedInvites}
        collapseInvites={() => setExpandedInvites(false)}
        expandInvites={() => setExpandedInvites(true)}
        displayedBookingInvitesLength={displayedBookingInvites.length}
      />
      <Table
        rowKey={(invitation) => invitation.invitationId}
        rowClassName={getRowClassName}
        columns={invitationsTableColumns}
        dataSource={displayedBookingInvites}
        pagination={false}
      />

      <GaragePickerModal
        booking={modalBooking}
        garages={garages}
        checkPopupOpen={popupOpen}
        closePopup={() => setPopupOpen(false)}
        loadingGarages={loadingGarages}
      />
    </GarageAllocationCard>
  );
}

export default GarageAllocation;
